import { DEFAULT_LANDING_PAGE_ACCENT_COLOR } from '@consts';
import UserAvatarIcon from '@icons/UserAvatarIcon';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { usePaymentAndOrder } from '../../payments/PaymentAndOrderContext';
import GoalLandingPageJumpToMainCTAButton from './GoalLandingPageJumpToMainCTAButton';
import { parseHighlightedText } from './highlighters';

export default function GoalLandingPageTestimonialsSection() {
  const { state } = usePaymentAndOrder();
  const accentColor = state.goal?.landingPage?.accentColor || DEFAULT_LANDING_PAGE_ACCENT_COLOR;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [overflowMap, setOverflowMap] = useState<boolean[]>([]);
  const carouselRef = useRef<HTMLDivElement>(null);
  const testimonialRefs = useRef<(HTMLDivElement | null)[]>([]);
  const quoteRefs = useRef<(HTMLDivElement | null)[]>([]);
  const targetSlideRef = useRef(0);

  const scrollToCenter = (index: number) => {
    const testimonialElement = testimonialRefs.current[index];
    const carouselElement = carouselRef.current;

    if (testimonialElement && carouselElement) {
      const carouselRect = carouselElement.getBoundingClientRect();
      const testimonialRect = testimonialElement.getBoundingClientRect();

      // Calculate the center position
      const testimonialCenter = testimonialRect.left + testimonialRect.width / 2;
      const carouselCenter = carouselRect.left + carouselRect.width / 2;
      const scrollOffset = testimonialCenter - carouselCenter;

      carouselElement.scrollTo({
        left: carouselElement.scrollLeft + scrollOffset,
        behavior: 'smooth'
      });
    }
  };

  const centerTestimonial = (index: number) => {
    targetSlideRef.current = index;
    setCurrentSlide(index);
    requestAnimationFrame(() => {
      scrollToCenter(index);
    });
  };

  useEffect(() => {
    if (state.goal?.landingPage?.testimonials && state.goal?.landingPage?.testimonials.length > 0) {
      // Sync state before scrolling to keep highlighted slide in sync
      targetSlideRef.current = 0;
      setCurrentSlide(0);

      const timer = setTimeout(() => scrollToCenter(0), 50);
      return () => clearTimeout(timer);
    }
  }, [state.goal?.landingPage?.testimonials]);

  // Detect overflow for each quote to conditionally show gradient
  useEffect(() => {
    const checkOverflow = () => {
      const newMap = quoteRefs.current.map((el) => !!el && el.scrollHeight > el.clientHeight);
      setOverflowMap(newMap);
    };

    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    quoteRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [state.goal?.landingPage?.testimonials]);

  // Sync currentSlide live when user scrolls horizontally
  useEffect(() => {
    const carouselElement = carouselRef.current;
    if (!carouselElement) return;

    let snapTimer: ReturnType<typeof setTimeout>;

    const handleScroll = () => {
      const carouselCenter =
        carouselElement.getBoundingClientRect().left +
        carouselElement.getBoundingClientRect().width / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;
      testimonialRefs.current.forEach((el, index) => {
        if (!el) return;
        const dist = Math.abs(
          el.getBoundingClientRect().left + el.getBoundingClientRect().width / 2 - carouselCenter
        );
        if (dist < closestDistance) {
          closestDistance = dist;
          closestIndex = index;
        }
      });
      // Live visual update only when the closest card actually changes
      if (closestIndex !== targetSlideRef.current) {
        targetSlideRef.current = closestIndex;
        setCurrentSlide(closestIndex);
      }
      // Snap to center after scrolling stops
      clearTimeout(snapTimer);
      snapTimer = setTimeout(() => {
        const el = testimonialRefs.current[targetSlideRef.current];
        if (!el || !carouselElement) return;
        const offset =
          el.getBoundingClientRect().left +
          el.getBoundingClientRect().width / 2 -
          (carouselElement.getBoundingClientRect().left +
            carouselElement.getBoundingClientRect().width / 2);
        if (Math.abs(offset) > 2) scrollToCenter(targetSlideRef.current);
      }, 150);
    };

    carouselElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      carouselElement.removeEventListener('scroll', handleScroll);
      clearTimeout(snapTimer);
    };
  }, []);

  if (
    !state.goal?.landingPage?.testimonials ||
    state.goal?.landingPage?.testimonials.length === 0
  ) {
    return null;
  }

  return (
    <div
      className={classNames(
        'flex flex-col items-center w-full',
        setupForDark('bg-[#2c2c2c]'),
        // RESPONSIVE STYLES
        'gap-xl @[640px]:gap-huge',
        'py-xl @[640px]:py-huge'
      )}
    >
      {/* Testimonials Carousel */}
      <div className='flex flex-col gap-xl items-center w-full'>
        <div
          ref={carouselRef}
          className={classNames(
            'flex gap-[20px] items-center  w-full overflow-x-auto py-[20px]',
            // Hide scrollbar
            '[scrollbar-width:none]', // Firefox
            '[-ms-overflow-style:none]', // IE and Edge
            '[&::-webkit-scrollbar]:hidden' // Chrome, Safari, Opera
          )}
        >
          {/* Large margin spacer at the beginning - extra space for centering first testimonials */}
          <div className='w-[50vw] shrink-0' />
          {state.goal?.landingPage?.testimonials.map((testimonial, index) => {
            const isCenter = index === currentSlide;
            return (
              <div
                key={index}
                ref={(el) => {
                  testimonialRefs.current[index] = el;
                }}
                onClick={() => centerTestimonial(index)}
                className={classNames(
                  'rounded-md shrink-0 p-xl transition-all duration-300 ease-in-out cursor-pointer hover:shadow-lg border ',
                  setupForDark('border-landing-card-border'),
                  setupForDark('bg-white'),
                  // RESPONSIVE STYLES
                  'w-[280px] @[640px]:w-[400px] @[1024px]:w-[800px]',
                  isCenter
                    ? 'scale-[1.05] @[640px]:scale-[1.15] mx-[10px] @[640px]:mx-[20px] @[1024px]:mx-[50px]'
                    : 'scale-100'
                )}
              >
                <div className='flex flex-col gap-md items-start w-full justify-between'>
                  <div className='relative'>
                    <div
                      ref={(el) => {
                        quoteRefs.current[index] = el;
                      }}
                      className={classNames(
                        'font-normal italic leading-[1.3] w-full',
                        'max-h-[128px] @[640px]:max-h-[120px] overflow-hidden',
                        setupForDark('text-black'),
                        'text-[16px] @[640px]:text-[18px]'
                      )}
                    >
                      &quot;
                      {parseHighlightedText(
                        testimonial.quote,
                        'text-black',
                        undefined,
                        accentColor
                      )}
                      &quot;
                    </div>
                    {overflowMap[index] && (
                      <div className='absolute bottom-0 left-0 right-0 h-[50px] bg-gradient-to-t from-white to-transparent pointer-events-none' />
                    )}
                  </div>
                  <div className='flex gap-xs items-center w-full'>
                    {testimonial.avatarUrl ? (
                      <img
                        src={testimonial.avatarUrl}
                        alt={testimonial.author}
                        className='w-[44px] h-[44px] rounded-full object-cover'
                      />
                    ) : (
                      <UserAvatarIcon size={44} className='shrink-0' />
                    )}
                    <div className='flex flex-col flex-1'>
                      <p
                        className={classNames(
                          'font-medium leading-[1.2] whitespace-nowrap',
                          setupForDark('text-black'),
                          'text-[16px] @[640px]:text-[18px]'
                        )}
                      >
                        {parseHighlightedText(
                          testimonial.author,
                          'text-black',
                          undefined,
                          accentColor
                        )}
                      </p>
                      <p
                        className={classNames(
                          'font-normal leading-[1.3]',
                          'text-[14px] @[640px]:text-md',
                          setupForDark('text-[#c5c5c5]')
                        )}
                      >
                        {parseHighlightedText(
                          testimonial.role,
                          'text-[#c5c5c5]',
                          undefined,
                          accentColor
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {/* Large margin spacer at the end */}
          <div className='w-[50vw] shrink-0' />
        </div>

        {/* Pagination Dots */}
        <div className='flex gap-sm items-center justify-center'>
          {state.goal?.landingPage?.testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => centerTestimonial(index)}
              className={`w-[12px] h-[12px] rounded-full transition-colors cursor-pointer p-0 ${
                index === currentSlide ? 'border-none' : 'bg-transparent border-2 border-solid'
              }`}
              style={{
                backgroundColor: index === currentSlide ? accentColor : 'transparent',
                borderColor: index === currentSlide ? 'transparent' : accentColor
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* CTA Button */}
      <GoalLandingPageJumpToMainCTAButton type='hero' />
    </div>
  );
}
